import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Calendar, Pencil, Trash2, ChevronRight } from "lucide-react";
import {
  useAdminGetExamSessionsQuery,
  useAdminCreateExamSessionMutation,
  useAdminUpdateExamSessionMutation,
  useAdminDeleteExamSessionMutation,
} from "../../api/examsApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

const ORDINALS = ["", "1st", "2nd", "3rd", "4th", "5th"];

interface Form { title: string; year: number; session_number: number }
const blank: Form = { title: "", year: new Date().getFullYear(), session_number: 1 };

export default function AdminExamSessionsPage() {
  const { data, isLoading, isError, refetch } = useAdminGetExamSessionsQuery();
  const [createSession, { isLoading: creating }] = useAdminCreateExamSessionMutation();
  const [updateSession, { isLoading: updating }] = useAdminUpdateExamSessionMutation();
  const [deleteSession, { isLoading: deleting }] = useAdminDeleteExamSessionMutation();

  // Reading the timetable is exam.access, which is what opens this route.
  // Creating, editing and deleting a session is exam.session.manage.
  const { can } = useAccess();
  const canManage = can.perm(PERMS.exam.sessionManage);

  const [showForm, setShowForm]     = useState(false);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<Form>(blank);
  const [error, setError]           = useState("");
  const [toast, setToast]           = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const sessions: any[] = data?.sessions ?? [];

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const openCreate = () => { setForm(blank); setEditId(null); setError(""); setShowForm(true); };
  const openEdit   = (s: any) => {
    setForm({ title: s.title, year: s.year, session_number: s.session_number });
    setEditId(s.id); setError(""); setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError("Title is required."); return; }
    setError("");
    try {
      if (editId !== null) {
        await updateSession({ id: editId, body: form }).unwrap();
        showToast("Session updated.");
      } else {
        await createSession(form).unwrap();
        showToast("Session created.");
      }
      setShowForm(false); refetch();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to save.");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSession(id).unwrap();
      setConfirmDel(null); showToast("Session deleted."); refetch();
    } catch (err: any) {
      showToast(err?.data?.message || "Delete failed.");
    }
  };

  return (
    <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
      {toast && (
        <div className="fixed top-6 right-6 z-50 px-4 py-3 rounded-2xl shadow-xl text-sm font-bold text-white bg-emerald-600">{toast}</div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900 uppercase tracking-widest">Exam Sessions</h1>
          <p className="text-xs text-slate-400 font-bold mt-1 uppercase tracking-widest">
            Each session groups all exams (TPC, GDE, Intermediate) running together
          </p>
        </div>
        {canManage && <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all"
        >
          <Plus className="w-3.5 h-3.5" /> New Session
        </button>}
      </div>

      {isLoading && (
        <div className="space-y-3">{[1,2,3].map(i=><div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse"/>)}</div>
      )}

      {isError && (
        <div className="bg-rose-50 border border-rose-200 rounded-[2rem] p-8 text-center">
          <p className="text-rose-600 font-black uppercase tracking-widest text-xs mb-2">Failed to load sessions</p>
          <p className="text-rose-400 text-xs mb-4">The server returned an error. Make sure all database migrations have been run (<code className="font-mono bg-rose-100 px-1 rounded">php artisan migrate</code>).</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-rose-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-rose-700">Retry</button>
        </div>
      )}

      {!isLoading && !isError && sessions.length === 0 && (
        <div className="bg-white rounded-[2rem] border border-slate-200 p-16 text-center">
          <p className="text-slate-400 font-black uppercase tracking-widest text-sm mb-4">No exam sessions yet</p>
          {canManage && <button onClick={openCreate} className="px-5 py-2.5 bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all">
            Create First Session
          </button>}
        </div>
      )}

      <div className="grid gap-4">
        {sessions.map((s: any) => (
          <div key={s.id} className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-6 flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center text-white flex-shrink-0">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-black text-slate-900 text-sm uppercase tracking-tight">{s.title}</h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                  {s.year} &nbsp;·&nbsp; {ORDINALS[s.session_number] ?? s.session_number} Diet
                  &nbsp;·&nbsp; {s.timetable_entries_count ?? 0} timetable entries
                </p>
                {/* Linked diets */}
                {s.diets?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {s.diets.map((d: any) => (
                      <span key={d.id} className="inline-flex px-2 py-0.5 rounded-lg bg-slate-100 text-slate-600 text-[9px] font-black uppercase tracking-widest">
                        {d.exam?.code ?? "—"}: {d.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to={`/exams/admin/sessions/${s.id}`}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all"
              >
                Manage <ChevronRight className="w-3 h-3" />
              </Link>
              {canManage && <button
                onClick={() => openEdit(s)}
                className="p-2.5 rounded-xl bg-slate-100 hover:bg-orange-50 hover:text-orange-600 transition-all"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>}
              {canManage && <button
                onClick={() => setConfirmDel(s.id)}
                className="p-2.5 rounded-xl bg-slate-100 hover:bg-rose-50 hover:text-rose-600 transition-all"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>}
            </div>
          </div>
        ))}
      </div>

      {/* Create / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">
                {editId !== null ? "Edit Session" : "New Exam Session"}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="e.g. NIQS 2026 1ST DIET PROFESSIONAL EXAMINATION"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Year *</label>
                  <input
                    type="number"
                    min="2020" max="2100"
                    value={form.year}
                    onChange={e => setForm({...form, year: Number(e.target.value)})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100"
                  />
                </div>
                <div>
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Diet Number *</label>
                  <select
                    value={form.session_number}
                    onChange={e => setForm({...form, session_number: Number(e.target.value)})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none"
                  >
                    {[1,2,3,4].map(n => (
                      <option key={n} value={n}>{ORDINALS[n]} Diet</option>
                    ))}
                  </select>
                </div>
              </div>
              {error && <p className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={creating || updating} className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50">
                  {creating || updating ? "Saving..." : editId !== null ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDel !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Delete Session?</h3>
            <p className="text-xs text-slate-500">All timetable entries for this session will also be deleted.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDel(null)} className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600">Cancel</button>
              <button disabled={deleting} onClick={() => handleDelete(confirmDel)} className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
